// Account Settings → Account Security + Devices & Sessions (1.27.70).
//
// Only what exists is shown (owner ruling 2026-09-30): passkeys and a per-device
// session list are left off. The two-factor switch opens the same dialogs as
// Settings → Privacy & Security, and "Sign out of all other devices" is the same
// server call as that page's "Sign out others".
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Switch, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useSession } from '@/hooks';
import { authService } from '@/core/services/authService';
import { deviceLabel } from '@/core/lib/deviceLabel';
import type { User } from '@/core/models';
import { ChangePasswordDialog } from '../../ChangePasswordDialog';
import { TwoFactorDisableDialog, TwoFactorEnableDialog } from '../../TwoFactorDialog';
import { CardHead, DashCard, DashRow, Pill } from '../../dashboard/DashCard';
import { RowHint, RowList } from '../../dashboard/dashStyles';

export function SecurityCard({ user }: { user: User }) {
  const { updateProfile } = useSession();
  const toast = useToast();
  const [changingPw, setChangingPw] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [codesLeft, setCodesLeft] = useState<number | null>(null);
  const twoFactor = !!user.twoFactorEnabled;

  useEffect(() => {
    if (!twoFactor) return;
    let cancelled = false;
    authService.getTwoFactorStatus().then(
      (s) => !cancelled && setCodesLeft(s.recoveryCodesRemaining),
      () => !cancelled && setCodesLeft(null),
    );
    return () => {
      cancelled = true;
    };
  }, [twoFactor]);

  async function toggleAlerts(next: boolean) {
    const ok = await updateProfile({ loginAlerts: next });
    toast(
      ok
        ? next
          ? 'Sign-in alerts are on'
          : 'Sign-in alerts are off'
        : 'Could not save. Please try again',
    );
  }

  return (
    <DashCard labelledBy="acct-security-title" testId="security-card">
      <CardHead
        icon="shield"
        title="Account Security"
        titleId="acct-security-title"
        aside={twoFactor ? undefined : <Pill tone="accent">Recommended</Pill>}
      />
      <RowList>
        <DashRow icon="lock" label="Change password" hint="Choose a new password for signing in">
          <Button
            variant="secondary"
            size="sm"
            aria-label="Change password"
            onClick={() => setChangingPw(true)}
          >
            Change
          </Button>
        </DashRow>
        <DashRow
          icon="smartphone"
          label="Two-factor authentication"
          hint={
            twoFactor
              ? `On. ${codesLeft != null ? `${codesLeft} recovery ${codesLeft === 1 ? 'code' : 'codes'} left.` : 'A code from your app at sign-in.'}`
              : 'Adds a code from an authenticator app at sign-in. Needed for a verified badge.'
          }
        >
          <Switch
            checked={twoFactor}
            onChange={(next) => (next ? setEnabling(true) : setDisabling(true))}
            label="Two-factor authentication"
          />
        </DashRow>
        <DashRow
          icon="bell"
          label="Sign-in alerts"
          hint="Get notified when your account signs in somewhere new"
        >
          <Switch
            checked={!!user.loginAlerts}
            onChange={(next) => void toggleAlerts(next)}
            label="Sign-in alerts"
          />
        </DashRow>
      </RowList>

      {changingPw && <ChangePasswordDialog onClose={() => setChangingPw(false)} />}
      <TwoFactorEnableDialog
        open={enabling}
        onClose={() => setEnabling(false)}
        onEnabled={() => {
          setEnabling(false);
          toast('Two-factor authentication is on');
        }}
      />
      <TwoFactorDisableDialog
        open={disabling}
        onClose={() => setDisabling(false)}
        onDisabled={() => setDisabling(false)}
      />
    </DashCard>
  );
}

const SignOutWrap = styled.div`
  margin-top: auto;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

export function SessionsCard() {
  const { signOutOtherSessions } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const here = deviceLabel(typeof navigator === 'undefined' ? '' : navigator.userAgent);

  async function signOutOthers() {
    setBusy(true);
    try {
      const cleared = await signOutOtherSessions();
      toast(
        cleared === 0
          ? 'No other devices were signed in'
          : `Signed out ${cleared} other ${cleared === 1 ? 'session' : 'sessions'}`,
      );
    } catch {
      toast('Could not sign out other devices. Please try again');
    } finally {
      setBusy(false);
    }
  }

  return (
    <DashCard labelledBy="acct-sessions-title" testId="sessions-card">
      <CardHead
        icon="monitor"
        title="Devices & Sessions"
        titleId="acct-sessions-title"
        subtitle="The session you are using now"
      />
      <RowList>
        <DashRow
          icon={here.mobile ? 'smartphone' : 'monitor'}
          label={`${here.browser} on ${here.os}`}
          hint="This device"
          testId="this-device"
        >
          <Pill tone="success" dot>
            Active now
          </Pill>
        </DashRow>
      </RowList>
      <SignOutWrap>
        <RowHint>
          Ends every other session: other browsers, other devices and the mobile app. This one stays
          signed in.
        </RowHint>
        <Button variant="danger" size="sm" onClick={() => void signOutOthers()} disabled={busy}>
          <Icon name="logout" size={14} />
          {busy ? 'Signing out…' : 'Sign out of all other devices'}
        </Button>
      </SignOutWrap>
    </DashCard>
  );
}
